import React, { useState, useEffect } from 'react';
import styles from './NetworkThrottler.module.css';
import { Wifi, WifiHigh, AlertTriangle, CheckCircle2, RotateCw } from 'lucide-react';
import { AnimatedShinyText } from './common/AnimatedShinyText';

export default function NetworkThrottler() {
  // 'fast' or 'slow'
  const [networkMode, setNetworkMode] = useState('fast');
  // 'idle', 'blocked', 'loading', 'gateway', 'success', 'crashed'
  const [appState, setAppState] = useState('idle');
  // Internal timer to handle cleanup
  const timerRef = React.useRef(null);

  const handleCheckout = () => {
    if (appState !== 'idle') return;
    
    if (networkMode === 'fast') {
      // Fast mode: succeed instantly
      setAppState('loading');
      timerRef.current = setTimeout(() => {
        setAppState('success');
      }, 600);
    } else {
      // Bhagalpur mode: A masterclass in frustration
      setAppState('blocked'); // UI Thread freezes
      
      timerRef.current = setTimeout(() => {
        setAppState('loading'); // Finally a spinner, but it's stuttering
        
        timerRef.current = setTimeout(() => {
          setAppState('gateway'); // Stuck in gateway limbo
          
          timerRef.current = setTimeout(() => {
            setAppState('crashed'); // The inevitable ANR
          }, 3000);
        }, 2000);
      }, 1200);
    }
  };

  const handleReset = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setAppState('idle');
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleModeSwitch = (mode) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setNetworkMode(mode);
    setAppState('idle');
  };

  return (
    <section className={styles.wrapper}>
      <div className={styles.inner}>
        
        {/* Left Side: Controls & Copy */}
        <div className={styles.controlsSide}>
          <AnimatedShinyText className="hp-section-badge">Interactive Demo</AnimatedShinyText>
          <h2 className={styles.title}>The "Controlled Room" is a Lie.</h2>
          <p className={styles.desc}>
            Your developers test on MacBooks and the latest iPhones on corporate Wi-Fi. 
            But your users? They are buying your product on a ₹8k Android device with a 
            flaky 3G connection on a train. <strong>Experience the difference.</strong>
          </p>

          <div className={styles.toggleBox}>
            <div className={styles.toggleHeader}>Simulate Network & Hardware</div>
            
            <button 
              className={`${styles.toggleBtn} ${networkMode === 'fast' ? styles.activeFast : ''}`}
              onClick={() => handleModeSwitch('fast')}
            >
              <div className={styles.btnIcon}><WifiHigh size={18} /></div>
              <div className={styles.btnText}>
                <span className={styles.btnTitle}>Bangalore (Office)</span>
                <span className={styles.btnSub}>5G • 8GB RAM • High-End</span>
              </div>
              {networkMode === 'fast' && <div className={styles.activeDot}></div>}
            </button>

            <button 
              className={`${styles.toggleBtn} ${networkMode === 'slow' ? styles.activeSlow : ''}`}
              onClick={() => handleModeSwitch('slow')}
            >
              <div className={styles.btnIcon}><Wifi size={18} /></div>
              <div className={styles.btnText}>
                <span className={styles.btnTitle}>Bhagalpur (Real World)</span>
                <span className={styles.btnSub}>3G • 2GB RAM • Budget Phone</span>
              </div>
              {networkMode === 'slow' && <div className={styles.activeDotSlow}></div>}
            </button>
          </div>
        </div>

        {/* Right Side: The Phone Showcase */}
        <div className={styles.phoneSide}>
          <div className={styles.phoneFrame}>
            <div className={styles.notch}></div>
            
            {/* Phone Screen UI */}
            <div className={`${styles.screen} ${appState === 'crashed' ? styles.screenDimmed : ''}`}>
              
              {/* Fake App Header */}
              <div className={styles.appHeader}>
                <div className={styles.hamburger}>
                  <span></span><span></span><span></span>
                </div>
                <div className={styles.brand}>ShopCart</div>
                <div className={styles.cartIcon}>🛒</div>
              </div>

              {/* Fake App Content */}
              <div className={styles.appBody}>
                <div className={styles.productImagePlaceholder}>
                  👟
                </div>
                <div className={styles.productInfo}>
                  <div className={styles.category}>MEN'S RUNNING</div>
                  <h3 className={styles.productName}>AeroGlide Pro Max</h3>
                  <div className={styles.rating}>★★★★☆ <span>(1,284)</span></div>
                  <div className={styles.price}>₹4,999 <span>₹7,999</span></div>
                  
                  <div className={styles.variants}>
                    <div className={styles.sizeBox}>UK 8</div>
                    <div className={styles.sizeBox}>UK 9</div>
                    <div className={styles.sizeBox}>UK 10</div>
                  </div>
                </div>
              </div>

              {/* Bottom Sticky Action */}
              <div className={styles.bottomBar}>
                <button 
                  className={`${styles.buyBtn} ${appState !== 'idle' ? styles.btnDisabled : ''} ${appState === 'blocked' ? styles.btnBlocked : ''} ${appState === 'success' ? styles.btnSuccess : ''}`}
                  onClick={handleCheckout}
                  disabled={appState !== 'idle'}
                >
                  {appState === 'idle' && 'Buy Now'}
                  {appState === 'blocked' && 'Buy Now'}
                  {appState === 'loading' && networkMode === 'fast' && <RotateCw className={styles.spin} size={20} />}
                  {appState === 'loading' && networkMode === 'slow' && <RotateCw className={styles.stutterSpin} size={20} />}
                  {appState === 'gateway' && <><RotateCw className={styles.frozenSpin} size={20} /> Processing...</>}
                  {appState === 'success' && <><CheckCircle2 size={20} /> Payment Success</>}
                  {appState === 'crashed' && 'Buy Now'}
                </button>
              </div>

            </div>

            {/* Android ANR Dialog (Crash State) */}
            {appState === 'crashed' && (
              <div className={styles.anrDialog}>
                <div className={styles.anrIcon}><AlertTriangle size={24} color="#F2CC60" /></div>
                <div className={styles.anrTitle}>ShopCart isn't responding</div>
                <div className={styles.anrDesc}>Do you want to close it?</div>
                <div className={styles.anrActions}>
                  <button onClick={handleReset}>Wait</button>
                  <button onClick={handleReset} style={{color: '#FF7B72'}}>Close app</button>
                </div>
              </div>
            )}

            {/* Success Overlay to allow reset easily */}
            {appState === 'success' && (
              <div className={styles.resetOverlay}>
                <button onClick={handleReset}>Reset Demo</button>
              </div>
            )}
            
          </div>
        </div>
        
      </div>
    </section>
  );
}
